import { css } from 'aphrodite';
import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import type { ISupportThread, ISupportThreadAttachment } from '../../../models/Support/ISupportThread';
import { ISupportTicket } from '../../../models/Support/ISupportTicket';
import { DeliveryStatus, ThreadType } from '../../../models/Support/SupportEnums';
import {
	formatFileSize,
	getInitials,
	getSupportContactProfilePicSrc,
	getRepComposer,
	getSupportUserProfilePicSrc,
} from '../../../utils/Support/supportHelpers';
import { Avatar } from '@HoudiniUI/Avatar';
import { EditItemPenIcon } from '../../../web/components/svgs/icons/EditItemPenIcon';
import { Button } from '../../../web/components-v2/Button';
import { MediaPreviewDialog, type PreviewMediaKind } from '../../../web/components-v2/MediaPreviewDialog';
import { Popover, PopoverAnchor, PopoverContent } from '../../../web/components-v2/Popover';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { getAttachmentMediaKind } from './attachmentMedia';
import { getContextNoteLabel } from './contextNote';
import { DownloadGlyph, PlayGlyph } from './inboxIcons';
import { NoteDeleteAction } from './NoteDeleteAction';
import { NoteEditForm } from './NoteEditForm';
import { styles } from './styles';
import {
	formatCompactDateTime,
	formatDateTime,
	formatShortDateTime,
	getConfidenceInfo,
} from './supportTicketPresentation';
import { renderThreadBodyHtml } from './threadBodyHtml';

export interface ThreadItemProps {
	thread: ISupportThread;
	ticket?: ISupportTicket;
	agentNameMap?: Record<string, string>;
	agentProfilePicMap?: Record<string, string>;
	agentProfilePicEmailMap?: Record<string, string>;
	currentUserId?: string;
	currentUserName?: string;
	currentUserEmail?: string;
	currentUserProfilePic?: string;
	currentAccountId?: string;
	onAcceptDraft?: (body: string, threadId?: string) => void;
	onRejectDraft?: (threadId: string) => void;
	draftStatus?: 'pending' | 'accepted' | 'rejected';
	/** Latest thread shows full body by default; older messages stay trimmed. */
	isLatestThread?: boolean;
	/** Downloads this message's attachments as one zip; the control is hidden when it is absent. */
	onDownloadAttachments?: () => void;
	isDownloadingAttachments?: boolean;
	/** Offers edit and delete on the current user's own notes; off where the thread is only being previewed. */
	allowOwnNoteActions?: boolean;
	/** Runs once a delete succeeds, before the note leaves the page, so the host can take focus from it. */
	onNoteDeleted?: () => void;
}

// The roster names most mentions; the thread's own names cover anyone off it, such as a CSM.
const buildThreadMentions = (
	thread: Pick<ISupportThread, 'mentionedUserIds' | 'mentionedUsers'>,
	agentNameMap: Record<string, string>,
	currentUserId: string | undefined,
	currentUserName: string | undefined
) => {
	const mentionedNames = new Map((thread.mentionedUsers ?? []).map(user => [user.userId, user.name]));
	return (thread.mentionedUserIds ?? [])
		.map(userId => {
			const label =
				agentNameMap[userId] || mentionedNames.get(userId) || (userId === currentUserId ? currentUserName : undefined);
			return label ? { label, userId } : undefined;
		})
		.filter((mention): mention is { label: string; userId: string } => !!mention);
};

export const ThreadItem: React.FC<ThreadItemProps> = ({
	thread,
	ticket,
	agentNameMap = {},
	agentProfilePicMap = {},
	agentProfilePicEmailMap = {},
	currentUserId,
	currentUserName,
	currentUserEmail,
	currentUserProfilePic,
	currentAccountId,
	onAcceptDraft,
	onRejectDraft,
	draftStatus,
	isLatestThread,
	onDownloadAttachments,
	isDownloadingAttachments,
	allowOwnNoteActions,
	onNoteDeleted,
}) => {
	const isPhoneLayout = useMediaQuery({ maxWidth: 767 });
	const isCustomer = thread.type === ThreadType.CustomerMessage;
	const repComposer = getRepComposer(thread, ticket);
	const isNote = thread.type === ThreadType.Note;
	const isAiDraft = thread.type === ThreadType.AiDraft;
	const isForward = thread.type === ThreadType.Forward;
	const isAgentAuthored =
		thread.type === ThreadType.AgentReply || isNote || isForward || thread.type === ThreadType.AutoAcknowledgement;
	const deliveryFailed =
		thread.deliveryStatus === DeliveryStatus.Bounced || thread.deliveryStatus === DeliveryStatus.Dropped;
	const [collapsed, setCollapsed] = React.useState(isAiDraft && draftStatus !== 'pending');

	const hasTrimmedVersion = thread.bodyTrimmed && thread.bodyTrimmed !== thread.body;
	const [showFull, setShowFull] = React.useState(!!isLatestThread);
	const contextNoteLabel = getContextNoteLabel(thread);
	const [contextNoteExpanded, setContextNoteExpanded] = React.useState(false);
	const threadBodyId = React.useId();
	// Attachment URLs are re-signed on every thread poll, so the preview holds the URL it was opened with.
	// Feeding the live prop straight to a player would reload it, and restart playback, every poll.
	const [previewMedia, setPreviewMedia] = React.useState<{
		src: string;
		alt: string;
		fileName?: string;
		kind: PreviewMediaKind;
		attachmentId?: string;
	} | null>(null);
	const wasLatestThreadRef = React.useRef(!!isLatestThread);
	const [editingNote, setEditingNote] = React.useState(false);
	const editButtonRef = React.useRef<HTMLButtonElement>(null);
	const refocusEditButtonRef = React.useRef(false);

	React.useEffect(() => {
		if (editingNote || !refocusEditButtonRef.current) return;
		refocusEditButtonRef.current = false;
		editButtonRef.current?.focus();
	}, [editingNote]);

	React.useEffect(() => {
		if (isLatestThread) {
			setShowFull(true);
		} else if (wasLatestThreadRef.current) {
			setShowFull(false);
		}
		wasLatestThreadRef.current = !!isLatestThread;
	}, [isLatestThread]);

	const openImagePreview = (target: EventTarget | null) => {
		if (!(target instanceof HTMLImageElement)) return false;
		const src = target.currentSrc || target.src;
		if (!src) return false;
		setPreviewMedia({ src, alt: target.alt || 'Email image', kind: 'image' });
		return true;
	};
	const handleThreadBodyClick = (event: React.MouseEvent<HTMLDivElement>) => {
		if (openImagePreview(event.target)) event.preventDefault();
	};
	const handleThreadBodyKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== 'Enter' && event.key !== ' ') return;
		if (openImagePreview(event.target)) event.preventDefault();
	};

	if (isAiDraft && draftStatus !== 'pending' && collapsed) {
		return (
			<div
				data-thread-id={thread.id}
				className={css(styles.thread, styles.threadSystem, styles.threadSystemClickable)}
				onClick={() => setCollapsed(false)}
			>
				AI Draft ({draftStatus}) &middot; {formatDateTime(thread.creationDate)} &mdash; Click to expand
			</div>
		);
	}
	const threadStyle = isCustomer
		? styles.threadCustomer
		: isNote
			? styles.threadNote
			: isAiDraft
				? styles.threadAiDraft
				: isForward
					? styles.threadForward
					: styles.threadAgent;
	const avatarStyle = repComposer
		? undefined
		: isCustomer
			? styles.threadAvatarCustomer
			: isNote
				? styles.threadAvatarNote
				: isAiDraft
					? styles.threadAvatarAiDraft
					: undefined;
	const displayBody = showFull ? thread.body || '' : thread.bodyTrimmed || thread.body || '';
	const threadMentions = buildThreadMentions(thread, agentNameMap, currentUserId, currentUserName);
	const bodyHtml = renderThreadBodyHtml(displayBody, { currentUserId, mentions: threadMentions });
	const shouldPreWrap = !isCustomer && !displayBody.includes('<p>') && !displayBody.includes('<br');
	const resolvedAuthorName =
		thread.authorName || (isAgentAuthored && thread.authorId ? agentNameMap[thread.authorId] : undefined);
	const contactName = isCustomer ? ticket?.contactName || ticket?.contactEmail : undefined;
	const customerName = resolvedAuthorName || thread.authorEmail || contactName;
	const displayAuthorName = isAiDraft ? 'AI Assistant' : repComposer?.name || customerName || 'Unknown sender';
	const currentUserEmailLower = currentUserEmail?.trim().toLowerCase();
	// Creator, not author: the server authorizes on the creator it stamped, which a client cannot set.
	const canManageNote = !!allowOwnNoteActions && isNote && !!currentUserId && thread.creator?.id === currentUserId;
	// The editor's mention roster is scoped to the ticket's mailbox, so a preview without a ticket can't edit.
	const canEditNote = canManageNote && !!ticket;
	const isEditingNote = canEditNote && editingNote;
	const finishEditingNote = () => {
		refocusEditButtonRef.current = true;
		setEditingNote(false);
	};
	const isCurrentUserAuthor =
		(!!thread.authorId && thread.authorId === currentUserId) ||
		(!!thread.authorEmail &&
			!!currentUserEmailLower &&
			thread.authorEmail.trim().toLowerCase() === currentUserEmailLower);
	const authorEmailProfilePic = thread.authorEmail
		? agentProfilePicEmailMap[thread.authorEmail.trim().toLowerCase()]
		: undefined;
	const authorUserProfilePicSrc = getSupportUserProfilePicSrc({
		profilePic: isCurrentUserAuthor ? currentUserProfilePic : undefined,
		authorProfilePic: thread.authorProfilePic,
		agentProfilePic: (thread.authorId ? agentProfilePicMap[thread.authorId] : undefined) || authorEmailProfilePic,
		userId: thread.authorId || (isCurrentUserAuthor ? currentUserId : undefined),
		accountId: currentAccountId,
	});
	const avatarImgSrc = isAiDraft
		? undefined
		: repComposer
			? getSupportUserProfilePicSrc({
					profilePic: repComposer.profilePic,
					agentProfilePic: agentProfilePicMap[repComposer.id],
					userId: repComposer.id,
					accountId: currentAccountId,
				})
			: isCustomer
				? authorUserProfilePicSrc || (ticket ? getSupportContactProfilePicSrc(ticket) : undefined)
				: authorUserProfilePicSrc;

	return (
		<div
			data-thread-id={thread.id}
			className={css(styles.thread, threadStyle, draftStatus === 'rejected' && styles.threadRejected)}
		>
			<div className={css(styles.threadHeader)}>
				<div className={css(styles.threadAuthorRow)}>
					<Avatar
						imgSrc={avatarImgSrc}
						altText={displayAuthorName}
						fallbackText={isAiDraft ? 'AI' : getInitials(displayAuthorName, repComposer?.email ?? thread.authorEmail)}
						smallText
						styles={[styles.threadAvatar, ...(avatarStyle ? [avatarStyle] : [])]}
					/>
					<div className={css(styles.threadAuthorMeta)}>
						<div className={css(styles.threadAuthorName)}>
							<span className={css(styles.threadAuthorNameText)}>{displayAuthorName}</span>
							{isNote && (
								<>
									{' '}
									<span className={css(styles.noteBadge)}>Note</span>
								</>
							)}
							{isForward && (
								<>
									{' '}
									<span className={css(styles.forwardBadge)}>Forwarded</span>
								</>
							)}
							{deliveryFailed && (
								<>
									{' '}
									<span
										className={css(styles.deliveryFailedBadge)}
										title={`This email was not delivered (${thread.deliveryStatus?.toLowerCase()}).`}
									>
										Couldn&#8217;t be delivered
									</span>
								</>
							)}
							{isAiDraft && (
								<>
									{' '}
									<span className={css(styles.aiDraftBadge)}>AI Draft</span>
									{draftStatus === 'accepted' && <span className={css(styles.draftAccepted)}> Accepted</span>}
									{draftStatus === 'rejected' && <span className={css(styles.draftRejectedLabel)}> Rejected</span>}
								</>
							)}
							{isPhoneLayout && (
								<span className={css(styles.threadTimeInline)}>{formatCompactDateTime(thread.creationDate)}</span>
							)}
						</div>
						{isAiDraft && (
							<div className={css(styles.threadAuthorEmail)}>
								AI-generated draft &mdash; review before sending
								{ticket?.aiConfidence != null &&
									(() => {
										const info = getConfidenceInfo(ticket.aiConfidence);
										return (
											<span
												className={css(styles.aiScoreBadge, info.style, styles.aiScoreBadgeInline)}
												title={ticket?.confidenceReasoning || ticket?.aiReasoning || undefined}
											>
												Confidence: {info.label} ({ticket.aiConfidence})
											</span>
										);
									})()}
							</div>
						)}
						{contextNoteLabel && (
							<div className={css(styles.threadAuthorEmail)}>
								{contextNoteLabel} &middot;{' '}
								<Button
									variant='link'
									size='sm'
									styles={[styles.contextNoteToggle]}
									aria-expanded={contextNoteExpanded}
									aria-controls={threadBodyId}
									onClick={() => setContextNoteExpanded(!contextNoteExpanded)}
								>
									{contextNoteExpanded ? 'Show less' : 'Show more'}
								</Button>
							</div>
						)}
						{isNote && thread.editedDate && (
							<div className={css(styles.threadAuthorEmail)} title={formatDateTime(thread.editedDate)}>
								Edited {formatShortDateTime(thread.editedDate)}
							</div>
						)}
						{repComposer && customerName && (
							<div className={css(styles.threadAuthorEmail)}>On behalf of {customerName}</div>
						)}
						{!isAiDraft && !repComposer && resolvedAuthorName && thread.authorEmail && (
							<div className={css(styles.threadAuthorEmail)}>From {thread.authorEmail}</div>
						)}
						{thread.toEmails?.length > 0 && (
							<div className={css(styles.threadRecipients)}>To: {thread.toEmails.join(', ')}</div>
						)}
						{thread.ccEmails?.length > 0 && (
							<div className={css(styles.threadRecipients)}>CC: {thread.ccEmails.join(', ')}</div>
						)}
						{thread.bccEmails?.length > 0 && (
							<div className={css(styles.threadRecipients)}>BCC: {thread.bccEmails.join(', ')}</div>
						)}
					</div>
				</div>
				{(!isPhoneLayout || canManageNote) && (
					<div className={css(styles.threadHeaderMeta)}>
						{!isPhoneLayout && <div className={css(styles.threadTime)}>{formatDateTime(thread.creationDate)}</div>}
						{canEditNote && !isEditingNote && (
							<Button
								ref={editButtonRef}
								variant='ghost'
								size='icon'
								styles={[styles.noteActionTrigger]}
								aria-label='Edit note'
								title='Edit note'
								onClick={() => setEditingNote(true)}
							>
								<EditItemPenIcon fillColor='currentColor' />
							</Button>
						)}
						{canManageNote && (
							<NoteDeleteAction ticketId={thread.ticketId} threadId={thread.id} onDeleted={onNoteDeleted} />
						)}
					</div>
				)}
			</div>
			{isAiDraft && draftStatus !== 'pending' ? (
				<div
					className={css(styles.collapsedToggle)}
					role='button'
					tabIndex={0}
					onClick={() => setCollapsed(true)}
					onKeyDown={e => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							setCollapsed(true);
						}
					}}
				>
					Hide draft &#9650;
				</div>
			) : null}
			{isEditingNote && ticket && currentUserId && (
				<NoteEditForm
					thread={thread}
					ticket={ticket}
					mentions={threadMentions}
					currentUserId={currentUserId}
					currentUserName={currentUserName ?? ''}
					currentAccountId={currentAccountId}
					onDone={finishEditingNote}
				/>
			)}
			<div
				id={threadBodyId}
				hidden={isEditingNote || (!!contextNoteLabel && !contextNoteExpanded)}
				className={`${css(styles.threadBody, shouldPreWrap && styles.threadBodyPreWrap)} thread-body-html`}
				onClick={handleThreadBodyClick}
				onKeyDown={handleThreadBodyKeyDown}
				dangerouslySetInnerHTML={{ __html: bodyHtml }}
			/>
			{hasTrimmedVersion && !isEditingNote && (
				<Button variant='ghost' size='sm' styles={[styles.showFullToggle]} onClick={() => setShowFull(!showFull)}>
					{showFull ? '▲ Show less' : '▼ Show full email'}
				</Button>
			)}
			{thread.attachments && thread.attachments.length > 0 && (
				<div className={css(styles.threadAttachments)}>
					{thread.attachments.map((attachment: ISupportThreadAttachment) => (
						<ThreadAttachmentChip
							key={attachment.id}
							attachment={attachment}
							onPreview={(src, alt, fileName, kind) =>
								setPreviewMedia({ src, alt, fileName, kind, attachmentId: attachment.id })
							}
						/>
					))}
					{thread.attachments.length > 1 && onDownloadAttachments && (
						<Button
							variant='ghost'
							size='sm'
							styles={[styles.attachmentDownloadAll]}
							isLoading={isDownloadingAttachments}
							title='Download every attachment on this message as a zip'
							onClick={onDownloadAttachments}
						>
							<DownloadGlyph className={css(styles.attachmentDownloadAllIcon)} />
							Download all ({thread.attachments.length})
						</Button>
					)}
				</div>
			)}
			{isAiDraft && draftStatus === 'pending' && (
				<div className={css(styles.draftActions)}>
					<Button
						variant='primary'
						size='sm'
						onClick={() => onAcceptDraft?.(thread.body || thread.bodyPlainText, thread.id)}
					>
						Accept Draft
					</Button>
					<Button variant='outline' size='sm' onClick={() => onRejectDraft?.(thread.id)}>
						Reject
					</Button>
				</div>
			)}
			{previewMedia && (
				<MediaPreviewDialog
					open
					kind={previewMedia.kind}
					src={previewMedia.src}
					latestSrc={thread.attachments?.find(a => a.id === previewMedia.attachmentId)?.url}
					alt={previewMedia.alt}
					fileName={previewMedia.fileName}
					onOpenChange={open => !open && setPreviewMedia(null)}
				/>
			)}
		</div>
	);
};

interface ThreadAttachmentChipProps {
	attachment: ISupportThreadAttachment;
	onPreview: (src: string, alt: string, fileName: string, kind: PreviewMediaKind) => void;
}

const ThreadAttachmentChip: React.FC<ThreadAttachmentChipProps> = ({ attachment, onPreview }) => {
	const [hoverPreviewOpen, setHoverPreviewOpen] = React.useState(false);
	const url = attachment.url;
	const mediaKind = getAttachmentMediaKind(attachment);
	const label = (
		<>
			<span className={css(styles.attachmentName)}>{attachment.fileName}</span>
			<span className={css(styles.attachmentSize)}>{formatFileSize(attachment.fileSize)}</span>
		</>
	);

	if (!url || mediaKind === 'file') {
		return (
			<a
				className={css(styles.attachmentChip)}
				href={url}
				target='_blank'
				rel='noopener noreferrer'
				title={attachment.fileName}
				onClick={event => {
					if (!url) event.preventDefault();
				}}
			>
				<span className={css(styles.attachmentIcon)}>📎</span>
				{label}
			</a>
		);
	}

	if (mediaKind === 'audio' || mediaKind === 'video') {
		return (
			<Button
				type='button'
				variant='outline'
				size='sm'
				styles={[styles.attachmentChip]}
				title={`Play ${attachment.fileName}`}
				onClick={() => onPreview(url, attachment.fileName, attachment.fileName, mediaKind)}
			>
				<PlayGlyph className={css(styles.attachmentPlayIcon)} />
				{label}
			</Button>
		);
	}

	return (
		<Popover open={hoverPreviewOpen} onOpenChange={setHoverPreviewOpen}>
			<PopoverAnchor asChild>
				<Button
					type='button'
					variant='outline'
					size='sm'
					styles={[styles.attachmentChip]}
					onMouseEnter={() => setHoverPreviewOpen(true)}
					onMouseLeave={() => setHoverPreviewOpen(false)}
					onFocus={() => setHoverPreviewOpen(true)}
					onBlur={() => setHoverPreviewOpen(false)}
					onClick={() => {
						setHoverPreviewOpen(false);
						onPreview(url, attachment.fileName, attachment.fileName, 'image');
					}}
				>
					<span className={css(styles.attachmentIcon)}>📎</span>
					{label}
				</Button>
			</PopoverAnchor>
			<PopoverContent
				side='top'
				align='start'
				sideOffset={THEME.spacing.sm}
				styles={[styles.attachmentHoverPreview]}
				onOpenAutoFocus={event => event.preventDefault()}
				onCloseAutoFocus={event => event.preventDefault()}
			>
				<img className={css(styles.attachmentHoverPreviewImage)} src={url} alt={`${attachment.fileName} preview`} />
			</PopoverContent>
		</Popover>
	);
};
